import { useNavigate } from 'react-router-dom'
import {
    ICONS,
    TASK_PRIORITY_LABELS,
    TASK_STATUSES,
} from '../../../common/constants'
import fetchAPI from '../../../common/fetchAPI'
import {
    copyToClipboard,
    formatDateTime,
    getHoursAndMinutesFromMinutes,
} from '../../../common/helpers'
import useAuth from '../../../common/hooks/useAuth'
import useMatchMutate from '../../../common/hooks/useMatchMutate'
import useToast from '../../../common/hooks/useToast'
import Button from '../../../components/common/Button'
import InlineStack from '../../../components/common/InlineStack'
import Tag from '../../../components/common/Tag'
import TaskStatusTag from './TaskStatusTag'
import TaskClientStatusTag from './TaskClientStatusTag'
import InlineStackItem from '../../../components/common/InlineStackItem'
import BlockStack from '../../../components/common/BlockStack'
import ContactCard from '../clients/ContactCard'
import OrgCard from '../../contacts/OrgCard'

export default function TaskItem({ task }) {
    const auth = useAuth()
    const setToast = useToast()
    const matchMutate = useMatchMutate()
    const navigate = useNavigate()

    const hasBugTag = task?.tags?.map((t) => t.toLowerCase()).includes('bug')
    // const hasClientTag = task?.tags
    //     ?.map((t) => t.toLowerCase())
    //     .includes('client')

    async function toggleBug(e) {
        e.preventDefault()
        e.stopPropagation()
        const url = `/v1/tasks/${task.id}`
        const body = {
            tags: hasBugTag
                ? task.tags.filter((t) => t.toLowerCase() !== 'bug')
                : [...task.tags, 'Bug'],
        }
        const { error } = await fetchAPI(url, body, 'PATCH')
        if (error) {
            setToast(error, 'alert')
            return
        }
        setToast('Saved')
        matchMutate(/^\/v1\/tasks/)
    }

    // async function toggleClient(e) {
    //     e.preventDefault()
    //     e.stopPropagation()
    //     const url = `/v1/tasks/${task.id}`
    //     const body = {
    //         tags: hasClientTag
    //             ? task.tags.filter((t) => t.toLowerCase() !== 'client')
    //             : [...task.tags, 'Client'],
    //     }
    //     const { error } = await fetchAPI(url, body, 'PATCH')
    //     if (error) {
    //         setToast(error, 'alert')
    //         return
    //     }
    //     setToast('Saved')
    //     matchMutate(/^\/v1\/tasks/)
    // }

    async function setItemPriority(priority, item) {
        // setMarkCompleteLoading(true)
        const url = `/v1/tasks/${item.id}`
        const { error } = await fetchAPI(url, { priority }, 'PATCH')
        // setMarkCompleteLoading(false)
        if (error) {
            setToast(error, 'alert')
            return
        }
        setToast('Saved')
        matchMutate(/^\/v1\/tasks/)
    }

    const priorityTag = (
        <Tag
            noMargin
            initValue={task.priority}
            outline
            onChange={(v) => setItemPriority(v, task)}
            options={
                !auth.isAdmin
                    ? []
                    : Object.entries(TASK_PRIORITY_LABELS).map(([k, v]) => ({
                          label: v.label,
                          value: k,
                          color: v.tagColor,
                      }))
            }
            color={TASK_PRIORITY_LABELS[task.priority].tagColor}
        >
            {TASK_PRIORITY_LABELS[task.priority].label}
            {/* {item.priority} */}
        </Tag>
    )

    const assignedHtml =
        (auth.isAdmin || auth.isStaff) &&
        ['team', 'role'].includes(task.target) &&
        task.assignedTeamMember ? (
            <BlockStack gap={'tiny'}>
                <div className="small text-subdued">{`Assigned to`}</div>
                <ContactCard user={task.assignedTeamMember} />
            </BlockStack>
        ) : null

    const creatorHtml =
        !auth.isClient && task.creator ? (
            <BlockStack gap={'tiny'}>
                <div className="small text-subdued">{`Created by (${formatDateTime(task.createdAt, true)})`}</div>
                <ContactCard user={task.creator} />
            </BlockStack>
        ) : null

    let homeworkHtml
    if (task.target === 'client') {
        homeworkHtml = (
            <div>
                <BlockStack gap={'tiny'}>
                    <div className="small text-subdued">{`Homework for`}</div>
                    {task.associatedClientType === 'User' ? (
                        <ContactCard user={task.associatedUserClient} />
                    ) : (
                        <OrgCard org={task.associatedOrgClient} />
                    )}
                </BlockStack>
            </div>
        )
    } else if (task.associatedClientId && (auth.isAdmin || auth.isStaff)) {
        homeworkHtml = (
            <div>
                <BlockStack gap={'tiny'}>
                    <div className="small text-subdued">{`Client`}</div>
                    {task.associatedClientType === 'User' ? (
                        <ContactCard user={task.associatedUserClient} />
                    ) : (
                        <OrgCard org={task.associatedOrgClient} />
                    )}
                </BlockStack>
            </div>
        )
    }

    const commentsHtml = task.commentsCount ? (
        <span className="icon-with-badge">
            <div
                role="button"
                onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    navigate(`/tasks/${task.id}#comments`)
                }}
            >
                <img
                    src={ICONS.MESSAGE_SQUARE_GRAY}
                    alt="mail"
                    width="24"
                    height="24"
                />
                <span className="icon-badge">{task.commentsCount}</span>
            </div>
        </span>
    ) : null

    let bugHtml
    if (Array.isArray(task.tags) && auth.isAdmin) {
        bugHtml = (
            <Button
                iconTop
                plain
                tiny
                icon={hasBugTag ? ICONS.BUG_GRAY : ICONS.BUG_ACTIVE}
                onClick={toggleBug}
                text={
                    <>
                        {hasBugTag ? (
                            <>
                                Not a <br />
                                bug
                            </>
                        ) : (
                            <>Bug</>
                        )}
                    </>
                }
            />
        )
    } else if (Array.isArray(task.tags) && hasBugTag) {
        bugHtml = (
            <img src={ICONS.BUG_ACTIVE} width={24} height={24} alt="Bug" />
        )
    }

    // let clientHtml
    // TODO: ?
    // if (Array.isArray(task.tags) && auth.isAdmin) {
    //     clientHtml = (
    //         <img
    //             onClick={toggleClient}
    //             role="button"
    //             src={hasClientTag ? ICONS.USER_ACTIVE : ICONS.USER_GRAY}
    //             width={24}
    //             height={24}
    //             alt={hasClientTag ? 'Disable client' : 'Enable client'}
    //         />
    //     )
    // } else if (Array.isArray(task.tags) && hasClientTag) {
    //     // clientHtml = (
    //     //     <img src={ICONS.USER_ACTIVE} width={24} height={24} alt="Client" />
    //     // )
    // }

    let title = task.title
    if (hasBugTag && !task.title.toLowerCase().startsWith('bug:')) {
        title = (
            <>
                <span className="text-subdued">Bug:&nbsp;</span>
                {task.title}
            </>
        )
    }
    // if (hasClientTag && !task.title.toLowerCase().startsWith('client:')) {
    //     title = (
    //         <>
    //             <span className="text-subdued">Client task:&nbsp;</span>
    //             {task.title}
    //         </>
    //     )
    // }

    const isAppTask = task.type && task.taskType !== 'USER_GENERATED'

    let dueDateAccent
    // If the task is overdue, show it in red,
    // if it's 15 days away, show it in orange,
    if (task.dueDate) {
        const dueDate = new Date(task.dueDate)
        const now = new Date()
        const diff = dueDate.getTime() - now.getTime()
        const days = Math.ceil(diff / (1000 * 3600 * 24))
        if (days < 0) {
            dueDateAccent = 'alert'
        } else if (days < 15) {
            dueDateAccent = 'warning'
        }
    }

    return (
        <div className="task-item">
            <InlineStack itemsStretch gap={'tiny'}>
                {!isAppTask && (
                    <InlineStackItem shrink0>
                        <BlockStack gap={'tiny'}>
                            {!!task.dueDate && (
                                <small
                                    className={`text-subdued ${dueDateAccent}`}
                                >
                                    {`Due ${formatDateTime(task.dueDate, true)}`}
                                </small>
                            )}
                            {!!task.timeLimitMinutes && (
                                <small className={`text-subdued`}>
                                    {`Limit ${
                                        getHoursAndMinutesFromMinutes(
                                            task.timeLimitMinutes,
                                        ).hours
                                    }h ${
                                        getHoursAndMinutesFromMinutes(
                                            task.timeLimitMinutes,
                                        ).minutes
                                    }m`}
                                </small>
                            )}
                            {task.status !== TASK_STATUSES.COMPLETE &&
                                task.status !== TASK_STATUSES.CANCELLED &&
                                (auth.isAdmin || auth.isStaff) && (
                                    <BlockStack>
                                        <InlineStack outline gap={'tiny'}>
                                            <img
                                                src={ICONS.GLOBE_ACTIVE}
                                                alt="globe"
                                                width={16}
                                                height={16}
                                            />
                                            <span className="text-subdued">
                                                {task.order}
                                            </span>
                                        </InlineStack>
                                    </BlockStack>
                                )}
                            {task.status !== TASK_STATUSES.COMPLETE &&
                                task.status !== TASK_STATUSES.CANCELLED &&
                                task.userOrder &&
                                (auth.isAdmin ||
                                    auth.isStaff ||
                                    auth.user.id ===
                                        task.assignedTeamMemberId ||
                                    (auth.user.id === task.associatedClientId &&
                                        task.associatedClientType ===
                                            'User')) && (
                                    <BlockStack>
                                        <InlineStack outline gap={'tiny'}>
                                            <img
                                                src={ICONS.USER_ACTIVE}
                                                alt="globe"
                                                width={16}
                                                height={16}
                                            />
                                            <span className="text-subdued">
                                                {task.userOrder}
                                            </span>
                                        </InlineStack>
                                    </BlockStack>
                                )}
                            {priorityTag}

                            <TaskStatusTag noMargin task={task} />
                            <TaskClientStatusTag noMargin task={task} />
                        </BlockStack>
                    </InlineStackItem>
                )}
                <BlockStack gap={'tiny'} spaceBetween>
                    <BlockStack gap={'tiny'}>
                        <h3>{title}</h3>
                        {!isAppTask && (
                            <InlineStack wrap gap={'tiny'} itemsStart>
                                <div>{assignedHtml}</div>
                                <div>{creatorHtml}</div>
                                <div>{homeworkHtml}</div>
                            </InlineStack>
                        )}
                        <InlineStack>
                            {task.tags?.length ? (
                                <InlineStack gap={'tiny'} wrap>
                                    {isAppTask && (
                                        <Tag square outline>
                                            APP
                                        </Tag>
                                    )}
                                    {task.tags
                                        .filter(
                                            (t) => t.toLowerCase() !== 'bug',
                                        )
                                        .filter(
                                            (t) => t.toLowerCase() !== 'client',
                                        )
                                        .map((tag, i) => (
                                            <Tag
                                                noMargin
                                                key={i}
                                                square
                                                outline
                                                href={
                                                    task.assignedTeamMemberId &&
                                                    !auth.isClient
                                                        ? `/users/${task.assignedTeamMemberId}?tag_task_list=${tag}&t_title=Tasks`
                                                        : undefined
                                                }
                                            >
                                                {tag}
                                            </Tag>
                                        ))}
                                </InlineStack>
                            ) : null}
                        </InlineStack>
                    </BlockStack>
                    <InlineStack itemsStart gap={'sm'}>
                        <InlineStack gap={'sm'} itemsStart>
                            {bugHtml}
                            <Button
                                iconTop
                                plain
                                tiny
                                alignRight
                                text={
                                    <>
                                        Copy
                                        <br />
                                        URL
                                    </>
                                }
                                icon={ICONS.COPY_ACTIVE}
                                onClick={(e) => {
                                    e.preventDefault()
                                    copyToClipboard(
                                        `${process.env.REACT_APP_PUBLIC_URL}/tasks/${task.id}`,
                                    )
                                    setToast('Copied to clipboard')
                                }}
                            />
                        </InlineStack>
                        {commentsHtml}
                    </InlineStack>
                </BlockStack>
                {/* <div className="task-item-description">
                            {task.descriptionHtml
                                ? getInnerTextFromHTML(task.descriptionHtml)
                                : ''}
                        </div> */}
                {/* <p className="subtitle">
                        {task.timeQuoteMinutes
                            ? `Estimated: ${
                                  getHoursAndMinutesFromMinutes(
                                      task.timeQuoteMinutes,
                                  ).hours
                              }h ${
                                  getHoursAndMinutesFromMinutes(
                                      task.timeQuoteMinutes,
                                  ).minutes
                              }m`
                            : null}
                    </p> */}
            </InlineStack>
        </div>
    )
}
